import { logger } from "../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { Search, UserPlus, ShieldOff, Settings, ArrowLeft, Ban, Filter, ChevronLeft, ChevronRight, Download, Mail, CheckCircle, FileCheck, ChevronUp, ChevronDown, Users } from 'lucide-react';
import { useAuth } from "../lib/auth.tsx";
import { supabase } from "../lib/db.ts";
import { UserRoleModal } from "../components/UserRoleModal.tsx";
import { UserAddModal } from "../components/UserAddModal.tsx";
import { Link } from 'react-router-dom';
import Papa from 'papaparse';

interface User {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_blocked: boolean;
  fica_verified: boolean;
  phone: string | null;
  is_sso_user: boolean;
  is_anonymous: boolean;
  wallet_balance: number | null;
  email_confirmed_at: string | null;
  affiliate_count?: number;
}

type SortColumn = 'email' | 'created_at' | 'last_sign_in_at' | 'wallet_balance' | 'affiliate_count' | 'role';
type SortDirection = 'ASC' | 'DESC';


export function AdminManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterBlocked, setFilterBlocked] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalWalletBalance, setTotalWalletBalance] = useState<number | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC');
  const pageSize = 10;
  useAuth();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search, filter, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterBlocked, sortColumn, sortDirection]);

  const fetchTotalWalletBalance = async () => {
    try {
      const { data, error } = await supabase.rpc('get_total_wallet_balance');
      if (error) {
        throw error;
      }
      if (data && data.length > 0) {
        setTotalWalletBalance(data[0].total_balance);
      }
    } catch (err) {
      logger.error('Error fetching total wallet balance:', err);
    }
  };

  const fetchUsers = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      
      logger.debug('Fetching users with params:', {
        search: debouncedSearchTerm,
        filter: filterBlocked,
        page: currentPage,
        pageSize
      });

      const { data, error: usersError } = await supabase
        .rpc('admin_list_users_paginated', {
          p_search_term: debouncedSearchTerm,
          p_filter_blocked: filterBlocked,
          p_page: currentPage,
          p_page_size: pageSize,
          p_sort_column: sortColumn,
          p_sort_direction: sortDirection
        });

      logger.error('Raw response:', { data, error: usersError });

      if (usersError) {
        logger.error('Supabase error:', usersError);
        throw usersError;
      }

      if (!data || !Array.isArray(data)) {
        logger.error('Invalid response format:', data);
        throw new Error('Invalid response format');
      }

      const response = data[0];
      logger.info('Processed response:', response);

      if (!response || !response.data || !Array.isArray(response.data)) {
        logger.error('Invalid data format:', response);
        throw new Error('Invalid data format');
      }

      setUsers(response.data);
      setTotalPages(Math.ceil(response.total / pageSize));
      setTotalUsers(response.total);
      setError(null);
    } catch (err) {
      logger.error('Error in fetchUsers:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to load users. Please ensure you have the correct permissions.'
      );
      setUsers([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  useEffect(() => {
    fetchUsers(true); // Pass true only for the very first load
    fetchTotalWalletBalance();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm || filterBlocked !== 'all') {
      fetchUsers(false);
    } else {
      // Handles clearing search or filter to return to initial state
      fetchUsers(false);
    }
  }, [currentPage, debouncedSearchTerm, filterBlocked, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // Set new column with default descending direction
      setSortColumn(column);
      setSortDirection('DESC');
    }
  };

  const SortableHeader = ({ column, children }: { column: SortColumn; children: React.ReactNode }) => {
    const isActive = sortColumn === column;
    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none group"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-2">
          {children}
          <div className="relative w-4 h-4">
            {isActive ? (
              sortDirection === 'ASC' ? (
                <ChevronUp className="w-4 h-4 text-wayja-teal" />
              ) : (
                <ChevronDown className="w-4 h-4 text-wayja-teal" />
              )
            ) : (
              <div className="opacity-30 group-hover:opacity-60 transition-opacity">
                <ChevronUp className="w-3 h-3 absolute top-0" />
                <ChevronDown className="w-3 h-3 absolute bottom-0" />
              </div>
            )}
          </div>
        </div>
      </th>
    );
  };

  // Log state update at debug level
  logger.debug('State updated:', {
    users: users.length,
    totalCount: totalUsers,
    currentPage: currentPage,
    totalPages: totalPages
  });

  const toggleBlockUser = async (userId: string, currentBlockedStatus: boolean) => {
    try {
      const { error: blockError } = await supabase.rpc('admin_toggle_user_block', {
        target_user_id: userId,
        block_status: !currentBlockedStatus
      });

      if (blockError) throw blockError;
      
      // Refresh the user list after successful operation
      fetchUsers(false);
    } catch (err) {
      logger.error('Error toggling user block status:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to update user block status.'
      );
    }
  };

  const manuallyVerifyUser = async (userEmail: string, userId: string) => {
    try {
      setLoading(true);
      setActionMessage(null);
      
      // Use database function to manually verify the user's email
      const { data, error: verifyError } = await supabase.rpc('admin_verify_user_email', {
        target_user_id: userId
      });

      if (verifyError) {
        throw verifyError;
      }

      // Log the action
      logger.info('Admin manually verified user email', {
        targetEmail: userEmail,
        targetUserId: userId,
        adminId: (await supabase.auth.getUser()).data.user?.id,
        result: data
      });

      // Show success message
      setActionMessage({ 
        type: 'success', 
        text: `Email manually verified for ${userEmail}` 
      });
      
      // Refresh user list to show updated status
      setTimeout(() => {
        fetchUsers(false);
        setActionMessage(null);
      }, 2000);
      
    } catch (err) {
      logger.error('Error manually verifying user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify user email.';
      setActionMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      
      setTimeout(() => {
        setActionMessage(null);
      }, 8000);
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async (userEmail: string, userId: string) => {
    try {
      setLoading(true);
      setActionMessage(null); // Clear any previous messages
      
      // Use regular resend with enhanced logging
      logger.info('Attempting admin resend for user:', { 
        email: userEmail, 
        userId: userId,
        redirectTo: 'https://wayja.me/auth/verify' 
      });
      
      const { data: resendData, error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
        options: {
          emailRedirectTo: `https://wayja.me/auth/verify`
        }
      });

      logger.info('Resend response:', { 
        hasData: !!resendData, 
        data: resendData,
        hasError: !!resendError,
        error: resendError 
      });

      if (resendError) {
        // Handle rate limit specifically
        if (resendError.message?.includes('rate_limit') || resendError.message?.includes('5 seconds')) {
          throw new Error('Please wait 5 seconds before requesting another email.');
        }
        throw resendError;
      }

      // Log the action
      logger.info('Admin resent verification email', {
        targetEmail: userEmail,
        targetUserId: userId,
        adminId: (await supabase.auth.getUser()).data.user?.id
      });

      // Show success with better UI
      setActionMessage({ 
        type: 'success', 
        text: `Verification email sent successfully to ${userEmail}` 
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setActionMessage(null);
      }, 5000);
      
    } catch (err) {
      logger.error('Error resending verification email:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend verification email.';
      setActionMessage({ 
        type: 'error', 
        text: errorMessage 
      });
      
      // Clear error message after 8 seconds
      setTimeout(() => {
        setActionMessage(null);
      }, 8000);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = () => {
    fetchUsers(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const exportToCsv = async () => {
    try {
      setLoading(true);
      
      // Fetch all users without pagination
      const { data, error: usersError } = await supabase
        .rpc('admin_list_users_paginated', {
          p_search_term: debouncedSearchTerm,
          p_filter_blocked: filterBlocked,
          p_page: 1,
          p_page_size: 1000000, // Large number to get all users
          p_sort_column: sortColumn,
          p_sort_direction: sortDirection
        });

      if (usersError) {
        logger.error('Error fetching users for export:', usersError);
        setActionMessage({ 
          type: 'error', 
          text: 'Failed to export users. Please try again.' 
        });
        setTimeout(() => setActionMessage(null), 5000);
        return;
      }

      if (!data || !Array.isArray(data) || !data[0] || !data[0].data) {
        logger.error('Invalid data format for export:', data);
        setActionMessage({ 
          type: 'error', 
          text: 'Failed to export users. Invalid data format.' 
        });
        setTimeout(() => setActionMessage(null), 5000);
        return;
      }

      const allUsers = data[0].data;
      
      const csv = Papa.unparse(allUsers.map((user: User) => ({
        ...user,
        wallet_balance: user.wallet_balance !== null ? user.wallet_balance.toFixed(2) : 'N/A'
      })));
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setActionMessage({ 
        type: 'success', 
        text: `Successfully exported ${allUsers.length} users to CSV` 
      });
      setTimeout(() => setActionMessage(null), 3000);
      
    } catch (err) {
      logger.error('Error exporting to CSV:', err);
      setActionMessage({ 
        type: 'error', 
        text: 'Failed to export users. Please try again.' 
      });
      setTimeout(() => setActionMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex justify-between items-center flex-grow">
          <h1 className="text-3xl font-bold">User Management</h1>
        </div>
      </div>
      <div className="flex justify-end items-center mb-4 gap-4">
        {totalWalletBalance !== null && (
          <div className="text-lg bg-white rounded-lg shadow-md p-4">
            Overall Wallet Balance: <strong>R {totalWalletBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </div>
        )}
        <button
          onClick={exportToCsv}
          className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
        >
          <Download className="w-5 h-5" />
          Export to CSV
        </button>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-wayja-teal text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          Add User
        </button>
      </div>

      {/* Action message display */}
      {actionMessage && (
        <div className={`mb-4 p-4 rounded-lg border ${
          actionMessage.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {actionMessage.type === 'success' ? '✅' : '❌'}
            </span>
            {actionMessage.text}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by email or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            />
            {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-5 w-5 border-b-2 border-wayja-teal"></div>}
          </div>
          <div className="flex items-center">
            <Filter className="text-gray-400 w-5 h-5 mr-2" />
            <select
              value={filterBlocked}
              onChange={(e) => setFilterBlocked(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            >
              <option value="all">All Users</option>
              <option value="active">Active Users</option>
              <option value="blocked">Blocked Users</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <SortableHeader column="email">Email</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FICA Status</th>
                <SortableHeader column="wallet_balance">Wallet Balance</SortableHeader>
                <SortableHeader column="affiliate_count">Affiliates</SortableHeader>
                <SortableHeader column="role">Role</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <SortableHeader column="created_at">Created</SortableHeader>
                <SortableHeader column="last_sign_in_at">Last Login</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users && users.length > 0 ? (
                users.map((userData) => (
                  <tr key={userData.id} className={userData.is_blocked ? "bg-red-50" : ""}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/admin/users/${userData.id}`}
                        className="text-sm text-wayja-blue hover:underline"
                      >
                        {userData.email}
                      </Link>
                      {userData.phone && (
                        <div className="text-sm text-gray-500">{userData.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        userData.email_confirmed_at ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {userData.email_confirmed_at ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        userData.fica_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {userData.fica_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {userData.wallet_balance !== null ? `R ${userData.wallet_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/admin/users/${userData.id}`}
                        className="flex items-center gap-2 text-sm text-gray-700 hover:text-wayja-teal"
                      >
                        <Users className="w-4 h-4" />
                        <span className="font-medium">{userData.affiliate_count || 0}</span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          userData.role === 'admin' || userData.role === 'superuser'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {userData.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        userData.is_blocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {userData.is_blocked ? 'Blocked' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(userData.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {userData.last_sign_in_at ? new Date(userData.last_sign_in_at).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedUser(userData)}
                          className="text-wayja-blue hover:text-wayja-blue-dark"
                          aria-label="Edit role"
                          title="Edit role"
                        >
                          <Settings className="w-5 h-5" />
                        </button>
                        
                        {/* Email verification actions - only show if not verified */}
                        {!userData.email_confirmed_at && (
                          <>
                            <button
                              onClick={() => resendVerificationEmail(userData.email, userData.id)}
                              className="text-blue-600 hover:text-blue-900"
                              aria-label="Resend verification email"
                              title="Resend verification email"
                            >
                              <Mail className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => manuallyVerifyUser(userData.email, userData.id)}
                              className="text-green-600 hover:text-green-900"
                              aria-label="Manually verify email"
                              title="Manually verify email (no email sent)"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => toggleBlockUser(userData.id, userData.is_blocked)}
                          className={`${
                            userData.is_blocked ? 'text-green-600 hover:text-green-900' : 'text-red-600 hover:text-red-900'
                          }`}
                          aria-label={userData.is_blocked ? 'Unblock user' : 'Block user'}
                          title={userData.is_blocked ? 'Unblock user' : 'Block user'}
                        >
                          {userData.is_blocked ? <ShieldOff className="w-5 h-5" /> : <Ban className="w-5 h-5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-4">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages} (Total: {totalUsers} users)
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserRoleModal
          userId={selectedUser.id}
          currentRole={selectedUser.role}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleRoleUpdate}
        />
      )}
      {showAddModal && (
        <UserAddModal
          onClose={() => setShowAddModal(false)}
          onAdd={() => fetchUsers(false)}
        />
      )}
    </div>
  );
}